import { EExamGroupStatus } from '@constants';
import { IExamGroup } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Wrapper,
  ItemWrapper,
  InfoWrapper,
  TitleWrapper,
  DateWrapper,
  IconWrapper,
  DaysCounter,
  DaysNumber,
  DaysText,
  GreenDot,
  StatusWrapper
} from './ExamGroupItem.styles';

import { StatusTitleItem } from '@/components/ExamGroup';
import { Icon } from '@/components/Icon';
import i18n from '@/i18n';
import { useProfile } from '@/store';

type Props = {
  examGroup: IExamGroup;
  onClick?: (examGroupId: string) => void;
};

export const ExamGroupItem: React.FC<Props> = ({ examGroup, onClick }) => {
  const { t } = useTranslation('StudentDashboardTranslations');
  const { t: tDefault } = useTranslation('DefaultTranslations');
  const { profile } = useProfile();

  const isInPlace = useMemo(() => {
    if (!examGroup.deadlineDates) return false;
    const startDate = examGroup?.deadlineDates?.[0];
    const endDate = examGroup?.deadlineDates?.[1];
    if (!startDate || !endDate) return false;

    const parsedStartDate = dateProvider.parse(startDate);
    const parsedEndDate = dateProvider.parse(endDate);
    if (isNaN(parsedStartDate.getTime()) || isNaN(parsedEndDate.getTime())) {
      return false;
    }
    return dateProvider.isWithinInterval(new Date(), {
      start: parsedStartDate,
      end: parsedEndDate
    });
  }, [examGroup.deadlineDates]);

  const daysLeft = useMemo(() => {
    if (!examGroup?.deadlineDates) return 0;
    const date = examGroup?.deadlineDates[1];
    return dateProvider.diffFromNow(date, 'days');
  }, [examGroup?.deadlineDates]);

  const formatDate = (date?: Date) => {
    if (!date) return null;
    const dayNumber = dateProvider.format(date, 'd');
    const month = dateProvider.parse(date).getMonth();
    const year = dateProvider.format(date, 'yyyy');
    const monthName = dateProvider.monthName(month, i18n.language);
    return `${dayNumber} ${monthName} ${year}`;
  };

  const fallbackText = tDefault(EExamGroupStatus.PENDING_DATE);

  const displayedDate = useMemo<string>(() => {
    const deadlineEnd = examGroup?.deadlineDates?.[1];
    if (deadlineEnd) {
      return formatDate(deadlineEnd) ?? fallbackText;
    }

    if (examGroup?.examDate) {
      return formatDate(examGroup.examDate) ?? fallbackText;
    }

    return fallbackText;
  }, [examGroup?.deadlineDates, examGroup?.examDate, i18n.language, fallbackText]);

  if (!profile) return null;

  return (
    <Wrapper>
      <ItemWrapper key={examGroup._id} onClick={() => onClick?.(examGroup._id)}>
        <IconWrapper>
          <Icon i="Event" color="purple7" />
          {isInPlace && <GreenDot />}
        </IconWrapper>
        <InfoWrapper>
          <TitleWrapper>{examGroup.name}</TitleWrapper>
          <DateWrapper>
            <StatusWrapper>
              <StatusTitleItem status={examGroup.status} />
              <span> · </span>
              {displayedDate}
            </StatusWrapper>
          </DateWrapper>
        </InfoWrapper>
        {daysLeft > 0 && (
          <DaysCounter>
            <DaysNumber>{daysLeft}</DaysNumber>
            <DaysText>{t('DAYS')}</DaysText>
          </DaysCounter>
        )}
      </ItemWrapper>
    </Wrapper>
  );
};
